import { DateTime } from "luxon";

export const GA4_TRACKING_COOKIE = "db_ga4_session";
export const GA4_RECENT_SESSION_CAPTURE_WINDOW_MS = 60000;
export function isGaAnalyticsDenied(raw: unknown): boolean {
  return typeof raw === "string" && /(?:^|,)analytics:no(?:,|$)/.test(raw);
}
export interface Ga4SessionCapture {
  gaClientId: string;
  gaSessionId: string;
  gaSessionCapturedAt: string;
}

/** Existing capture paths store the _ga cookie; MP needs its client-id suffix. */
export function normalizeGaClientId(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const value = raw.trim().replace(/^GA\d+\.\d+\./, "");
  return /^\d+\.\d+$/.test(value) ? value : undefined;
}

/** Recent capture approximates an active session; the property's actual timeout is unknown. */
export function resolveRecentGaSessionId(
  tracking: Partial<Pick<Ga4SessionCapture, "gaSessionId" | "gaSessionCapturedAt">> | undefined,
  eventMs: number,
  nowMs: number
): number | undefined {
  if (!Number.isFinite(eventMs) || !Number.isFinite(nowMs)) return undefined;
  const session = tracking?.gaSessionId;
  const capturedAt = tracking?.gaSessionCapturedAt;
  if (typeof session !== "string" || !/^\d{1,16}$/.test(session) || typeof capturedAt !== "string")
    return undefined;
  const sessionId = Number(session);
  const sessionStartMs = sessionId * 1000;
  const capturedMs = DateTime.fromISO(capturedAt).toMillis();
  if (
    Number.isSafeInteger(sessionId) &&
    sessionStartMs > 0 &&
    sessionStartMs <= capturedMs &&
    capturedMs <= eventMs &&
    eventMs - capturedMs <= GA4_RECENT_SESSION_CAPTURE_WINDOW_MS &&
    nowMs - sessionStartMs <= 24 * 3600000
  )
    return sessionId;
  return undefined;
}

export function parseGa4SessionCapture(raw: unknown): Ga4SessionCapture | undefined {
  if (typeof raw !== "string") return undefined;
  try {
    const value = JSON.parse(raw);
    const gaClientId = normalizeGaClientId(value.gaClientId);
    if (
      !gaClientId ||
      typeof value.gaSessionId !== "string" ||
      !/^\d{1,16}$/.test(value.gaSessionId) ||
      typeof value.gaSessionCapturedAt !== "string" ||
      !DateTime.fromISO(value.gaSessionCapturedAt).isValid
    )
      return undefined;
    return {
      gaClientId,
      gaSessionId: value.gaSessionId,
      gaSessionCapturedAt: value.gaSessionCapturedAt,
    };
  } catch {
    return undefined;
  }
}
